import { Button, Heading, Typography } from '@altify/components';
import {
  formatAssetAmount,
  getDisplayAssetCode,
  useContent,
} from '@altify/utils';
import ErrorImage from '../../assets/Error.svg';
import { tradeSharedContent } from '../../tradeShared.content';
import { submitOrderContent } from './SubmitOrderModal.content';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import CurrencyIcon from 'app/_authenticated/trade/components/CurrencyIcon/CurrencyIcon';
import { DialogOrBottomSheet } from 'components/DialogOrBottomSheet/DialogOrBottomSheet';
import * as loaderAnimationData from '../../assets/lottie/altify-loader-blue.json';
import * as successAnimationData from '../../assets/lottie/altify-loader-success-white.json';

const SubmitOrderModal = ({
  type,
  fromAmount,
  toAmount,
  price,
  fromProductCode,
  toProductCode,
  tradeComplete,
  tradeError,
  onClose,
  retryTrade,
}: {
  type: 'buy' | 'sell';
  fromAmount: number;
  toAmount: number;
  price: number;
  tradeComplete: boolean;
  tradeError: boolean;
  fromProductCode: string;
  toProductCode: string;
  onClose: () => void;
  retryTrade: () => void;
}) => {
  const localizedContent = useContent(submitOrderContent, tradeSharedContent);
  const navigate = useNavigate();
  const router = useRouter();

  function handleClose() {
    onClose?.();
    if (tradeComplete) {
      navigate({ to: '/products' });
    }
  }

  return (
    <DialogOrBottomSheet
      dismissable={tradeComplete || tradeError}
      onClose={handleClose}
      blurEnabled={true}
      backgroundColor={tradeComplete ? '#626AFF' : '#0C1C2C'}
      footerContent={
        <>
          {tradeError ? (
            <div className="w-full items-center px-4 pb-2">
              <Button
                size="large"
                color="primary"
                onPress={retryTrade}
                text={localizedContent.retry}
                className="mt-4 w-full"
              />
              <Button
                size="large"
                color="secondary"
                onPress={() => {
                  onClose?.();
                  router.history.back();
                }}
                text={localizedContent.cancelTrade}
                className="my-4 w-full"
              />
            </div>
          ) : (
            tradeComplete && (
              <div className="w-full items-center px-4 pb-4">
                <Button
                  size="large"
                  color={type == 'buy' ? 'secondary' : 'white'}
                  onPress={() => {
                    onClose?.();
                    navigate({ to: '/products' });
                  }}
                  text={localizedContent.exploreProducts}
                  className="mt-4 w-full"
                />
                {type == 'buy' && (
                  <Button
                    size="large"
                    color="white"
                    onPress={() => {
                      onClose?.();
                      navigate({ to: `/trade/recurring-buy?productCode=${toProductCode}` });
                    }}
                    text={localizedContent.createRecurringBuy}
                    className="my-4 w-full"
                  />
                )}
              </div>
            )
          )}
        </>
      }>
      <div className="flex w-full flex-col items-center px-4 py-4">
        <div className="w-full items-center justify-center">
          {tradeError ? (
            <div className="flex flex-col items-center justify-center mb-3 mt-5">
              <img src={ErrorImage} />
              <Heading level={5} className="mt-4 text-center">
                {localizedContent.error}
              </Heading>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="h-24">
                <DotLottieReact
                  data={
                    tradeComplete ? successAnimationData : loaderAnimationData
                  }
                  autoplay={true}
                  loop={!tradeComplete}
                  key={tradeComplete ? 'success' : 'loader'}
                />
              </div>

              <Heading level={4} className="mb-2">
                {tradeComplete
                  ? localizedContent.tradeComplete
                  : localizedContent.executingTrade}
              </Heading>
              {!tradeComplete && (
                <Typography size="large" className="text-neutral-300">
                  {localizedContent.thisMayTakeAMoment}
                </Typography>
              )}
              <div className="mb-10" />
              <CurrencyIcon
                size={48}
                code={toProductCode}
                invertedColors={tradeComplete}
              />

              <Typography size="large" className="mt-4 text-white">
                {tradeComplete
                  ? type === 'sell'
                    ? localizedContent.received
                    : localizedContent.bought
                  : type === 'sell'
                    ? localizedContent.receiving
                    : localizedContent.buying}
              </Typography>
              <Heading level={2}>
                {formatAssetAmount(toAmount, toProductCode, {
                  showSpacer: true,
                })}
              </Heading>
              {!tradeComplete && (
                <Typography size="large" weight="medium" className="mt-8">
                  {`1 ${getDisplayAssetCode(
                    type === 'buy' ? toProductCode : fromProductCode,
                  )} = ${formatAssetAmount(
                    price,
                    type === 'buy' ? fromProductCode! : toProductCode!,
                    { showSpacer: true },
                  )}`}
                </Typography>
              )}
            </div>
          )}
        </div>
      </div>
    </DialogOrBottomSheet>
  );
};

export default SubmitOrderModal;
